// Configuración de límites por plan
// Esta es una utilidad compartida que puede usarse en Worker, Edge Functions y cliente

export type PlanType = 'free' | 'pro' | 'enterprise'

export interface PlanLimites {
  maxTurnosMes: number
  maxEmpleados: number
  maxClientes: number
  recordatoriosAuto: boolean
  pushNotificaciones: boolean
  exportarCSV: boolean
  personalizacionMarca: boolean
  apiAccess: boolean
}

export const PLANES: Record<PlanType, PlanLimites> = {
  free: {
    maxTurnosMes: 50,
    maxEmpleados: 1,
    maxClientes: 100,
    recordatoriosAuto: false,
    pushNotificaciones: false,
    exportarCSV: false,
    personalizacionMarca: false,
    apiAccess: false,
  },
  pro: {
    maxTurnosMes: 500,
    maxEmpleados: 5,
    maxClientes: 1000,
    recordatoriosAuto: true,
    pushNotificaciones: true,
    exportarCSV: true,
    personalizacionMarca: true,
    apiAccess: false,
  },
  enterprise: {
    maxTurnosMes: -1, // ilimitado
    maxEmpleados: -1,
    maxClientes: -1,
    recordatoriosAuto: true,
    pushNotificaciones: true,
    exportarCSV: true,
    personalizacionMarca: true,
    apiAccess: true,
  },
}

export function getPlanLimites(plan: string | null | undefined, opciones?: { vitaliciaGratis?: boolean }): PlanLimites {
  // Si tiene suscripción vitalicia gratis, tratar como enterprise (ilimitado)
  if (opciones?.vitaliciaGratis) {
    return PLANES.enterprise
  }
  const planKey = (plan?.toLowerCase() as PlanType) || 'free'
  return PLANES[planKey] || PLANES.free
}

export function esIlimitado(valor: number): boolean {
  return valor === -1
}

export function formatearLimite(valor: number): string {
  return esIlimitado(valor) ? 'Ilimitado' : valor.toString()
}

// Verificar si se puede crear un recurso
export interface ResultadoVerificacion {
  permitido: boolean
  mensaje?: string
  limite?: number
  actual?: number
}

export async function verificarLimiteTurnos(
  supabase: any,
  tenantId: string,
  plan: string,
  opciones?: { vitaliciaGratis?: boolean }
): Promise<ResultadoVerificacion> {
  const limites = getPlanLimites(plan, opciones)
  if (esIlimitado(limites.maxTurnosMes)) return { permitido: true }

  // Contar turnos del mes actual (no cancelados)
  const inicioMes = new Date()
  inicioMes.setDate(1)
  inicioMes.setHours(0, 0, 0, 0)
  const finMes = new Date(inicioMes)
  finMes.setMonth(finMes.getMonth() + 1)

  const { count, error } = await supabase
    .from('turnos')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .gte('inicio', inicioMes.toISOString())
    .lt('inicio', finMes.toISOString())
    .neq('estado', 'cancelado')

  if (error) {
    console.error('Error contando turnos:', error)
    return { permitido: true } // Fail open
  }

  const actual = count || 0
  if (actual >= limites.maxTurnosMes) {
    return {
      permitido: false,
      mensaje: `Límite de ${limites.maxTurnosMes} turnos/mes alcanzado (${actual}/${limites.maxTurnosMes}). Actualiza a Pro para más.`,
      limite: limites.maxTurnosMes,
      actual,
    }
  }

  return { permitido: true, limite: limites.maxTurnosMes, actual }
}

export async function verificarLimiteEmpleados(
  supabase: any,
  tenantId: string,
  plan: string,
  opciones?: { vitaliciaGratis?: boolean }
): Promise<ResultadoVerificacion> {
  const limites = getPlanLimites(plan, opciones)
  if (esIlimitado(limites.maxEmpleados)) return { permitido: true }

  const { count, error } = await supabase
    .from('empleados')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('activo', true)

  if (error) {
    console.error('Error contando empleados:', error)
    return { permitido: true }
  }

  const actual = count || 0
  if (actual >= limites.maxEmpleados) {
    return {
      permitido: false,
      mensaje: `Límite de ${limites.maxEmpleados} empleado(s) alcanzado (${actual}/${limites.maxEmpleados}). Actualiza a Pro para más.`,
      limite: limites.maxEmpleados,
      actual,
    }
  }

  return { permitido: true, limite: limites.maxEmpleados, actual }
}

export async function verificarLimiteClientes(
  supabase: any,
  tenantId: string,
  plan: string,
  opciones?: { vitaliciaGratis?: boolean }
): Promise<ResultadoVerificacion> {
  const limites = getPlanLimites(plan, opciones)
  if (esIlimitado(limites.maxClientes)) return { permitido: true }

  const { count, error } = await supabase
    .from('clientes')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)

  if (error) {
    console.error('Error contando clientes:', error)
    return { permitido: true }
  }

  const actual = count || 0
  if (actual >= limites.maxClientes) {
    return {
      permitido: false,
      mensaje: `Límite de ${limites.maxClientes} clientes alcanzado (${actual}/${limites.maxClientes}). Actualiza a Pro para más.`,
      limite: limites.maxClientes,
      actual,
    }
  }

  return { permitido: true, limite: limites.maxClientes, actual }
}

// Función genérica para el Worker (sin supabase client)
export async function verificarLimitesWorker(
  env: any,
  tenantId: string,
  plan: string,
  tipo: 'turnos' | 'empleados' | 'clientes',
  opciones?: { vitaliciaGratis?: boolean }
): Promise<ResultadoVerificacion> {
  const limites = getPlanLimites(plan, opciones)

  const ahora = new Date()
  const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1)
  const finMes = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 1)

  const config = {
    turnos: { max: limites.maxTurnosMes, tabla: 'turnos', filtroExtra: `&estado=neq.cancelado&inicio=gte.${inicioMes.toISOString()}&inicio=lt.${finMes.toISOString()}` },
    empleados: { max: limites.maxEmpleados, tabla: 'empleados', filtroExtra: '&activo=eq.true' },
    clientes: { max: limites.maxClientes, tabla: 'clientes', filtroExtra: '' },
  }[tipo]

  if (esIlimitado(config.max)) return { permitido: true }

  const url = `https://${env.SUPABASE_PROJECT_REF}.supabase.co/rest/v1/${config.tabla}?tenant_id=eq.${tenantId}&select=id${config.filtroExtra}`
  const resp = await fetch(url, {
    headers: {
      apikey: env.SUPABASE_SERVICE_ROLE,
      Authorization: `Bearer ${env.SUPABASE_SERVICE_ROLE}`,
      Prefer: 'count=exact',
      Accept: 'application/json',
    },
  })

  if (!resp.ok) return { permitido: true }

  const count = parseInt(resp.headers.get('content-range')?.split('/')[1] || '0')

  if (count >= config.max) {
    return {
      permitido: false,
      mensaje: `Límite del plan Free: ${config.max} ${tipo}/${tipo === 'turnos' ? 'mes' : ''} (actual: ${count})`,
      limite: config.max,
      actual: count,
    }
  }

  return { permitido: true, limite: config.max, actual: count }
}